import {
  ExercisePropertyType,
  TrainingSetItemStatus,
} from '../../shared/constants';
import { IExercise } from '../../types/interfaces/exercise';
import { IExerciseProperty } from '../../types/interfaces/exercise-property';
import { ITraining } from '../../types/interfaces/training';

export interface IRadarChartData {
  muscleGroup: string;
  value: number;
  fullMark: number;
}

export interface ISkillsRadarChartData {
  skill: string;
  value: number;
  fullMark: number;
}

export function getPolarRadiusTicks(max: number, segments: number): number[] {
  const safeSegments = Math.max(1, segments);
  const step = max / safeSegments;
  return Array.from({ length: safeSegments }, (_, i) => step * (i + 1));
}

function getUniqueExerciseIdsWithFinishedWork(
  training: ITraining | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  training?.trainingSets?.forEach((trainingSet) => {
    const finishedItemsCount =
      trainingSet.trainingSetItems?.filter(
        (item) => item.status === TrainingSetItemStatus.finished,
      ).length || 0;

    if (finishedItemsCount === 0 || !trainingSet.exerciseId) {
      return;
    }

    ids.add(trainingSet.exerciseId);
  });
  return ids;
}

const volumeOfMainMuscleGroup = 20;
const volumeOfAdditionalMuscleGroup = 7;
const safetyFactor = 1.05;

export function getRadarChartDataFromTraining(
  training: ITraining | null | undefined,
  exercises: IExercise[] | undefined,
  exerciseProperties: IExerciseProperty[] | undefined,
): IRadarChartData[] {
  if (!training || !exerciseProperties || !exercises) {
    return [];
  }

  const muscleGroupProperties = exerciseProperties.filter(
    (prop) => prop.type === ExercisePropertyType.muscleGroup,
  );

  if (muscleGroupProperties.length === 0) {
    return [];
  }

  const muscleGroupValues: Record<string, number> = {};
  muscleGroupProperties.forEach((prop) => {
    muscleGroupValues[prop.id] = 0;
  });

  training.trainingSets?.forEach((trainingSet) => {
    const exercise = exercises?.find((e) => e.id === trainingSet.exerciseId);
    if (!exercise) {
      return;
    }

    const finishedItemsCount =
      trainingSet.trainingSetItems?.filter(
        (item) => item.status === TrainingSetItemStatus.finished,
      ).length || 0;

    if (finishedItemsCount === 0) {
      return;
    }

    if (exercise.mainMuscleGroup?.parentId) {
      const matchingMuscleGroupId = exercise.mainMuscleGroup.parentId;
      if (muscleGroupValues[matchingMuscleGroupId] !== undefined) {
        muscleGroupValues[matchingMuscleGroupId] +=
          volumeOfMainMuscleGroup * finishedItemsCount;
      }
    } else {
      Object.keys(muscleGroupValues).forEach((groupId) => {
        muscleGroupValues[groupId] += 5 * finishedItemsCount;
      });
    }

    if (exercise.additionalMuscles && exercise.additionalMuscles.length > 0) {
      exercise.additionalMuscles.forEach((additionalMuscle) => {
        if (additionalMuscle?.parentId) {
          const matchingMuscleGroupId = additionalMuscle.parentId;
          if (muscleGroupValues[matchingMuscleGroupId] !== undefined) {
            muscleGroupValues[matchingMuscleGroupId] +=
              volumeOfAdditionalMuscleGroup * finishedItemsCount;
          }
        } else {
          Object.keys(muscleGroupValues).forEach((groupId) => {
            muscleGroupValues[groupId] += 5 * finishedItemsCount;
          });
        }
      });
    }
  });

  const maxValue = Math.max(...Object.values(muscleGroupValues), 0);
  const fullMark = maxValue > 0 ? Math.ceil(maxValue * safetyFactor) : 100;

  return muscleGroupProperties.map((prop) => ({
    muscleGroup: prop.name || prop.nameKey,
    value: muscleGroupValues[prop.id] || 0,
    fullMark,
  }));
}

export function getSummarizedRadarChartDataFromTrainings(
  trainings: ITraining[] | null | undefined,
  exercises: IExercise[] | undefined,
  exerciseProperties: IExerciseProperty[] | undefined,
): IRadarChartData[] {
  if (!trainings || trainings.length === 0 || !exerciseProperties) {
    return [];
  }

  const muscleGroupProperties = exerciseProperties.filter(
    (prop) => prop.type === ExercisePropertyType.muscleGroup,
  );

  if (muscleGroupProperties.length === 0) {
    return [];
  }

  const aggregatedMuscleGroupValues: Record<string, number> = {};
  muscleGroupProperties.forEach((prop) => {
    aggregatedMuscleGroupValues[prop.id] = 0;
  });

  trainings.forEach((training) => {
    const trainingData = getRadarChartDataFromTraining(
      training,
      exercises,
      exerciseProperties,
    );

    trainingData.forEach((dataPoint) => {
      const muscleGroupProperty = muscleGroupProperties.find(
        (prop) => (prop.name || prop.nameKey) === dataPoint.muscleGroup,
      );
      if (muscleGroupProperty) {
        aggregatedMuscleGroupValues[muscleGroupProperty.id] += dataPoint.value;
      }
    });
  });

  const maxValue = Math.max(...Object.values(aggregatedMuscleGroupValues), 0);
  const fullMark = maxValue > 0 ? Math.ceil(maxValue * 1.2) : 100; // Add 20% padding

  return muscleGroupProperties.map((prop) => ({
    muscleGroup: prop.name || prop.nameKey,
    value: aggregatedMuscleGroupValues[prop.id] || 0,
    fullMark,
  }));
}

const skillsSafetyFactorSummarize = 1.2;

/**
 * Per training: each exercise with finished work counts once; +1 per matching catalog skill on that exercise.
 */
export function getSkillsRadarChartDataFromTraining(
  training: ITraining | null | undefined,
  exercises: IExercise[] | undefined,
  exerciseProperties: IExerciseProperty[] | undefined,
): ISkillsRadarChartData[] {
  if (!training || !exerciseProperties || !exercises) {
    return [];
  }

  const skillProperties = exerciseProperties.filter(
    (prop) => prop.type === ExercisePropertyType.developedSkill,
  );

  if (skillProperties.length === 0) {
    return [];
  }

  const skillCounts: Record<string, number> = {};
  skillProperties.forEach((prop) => {
    skillCounts[prop.id] = 0;
  });

  const exerciseIds = getUniqueExerciseIdsWithFinishedWork(training);
  exerciseIds.forEach((exerciseId) => {
    const exercise = exercises.find((e) => e.id === exerciseId);
    exercise?.developedSkills?.forEach((skill) => {
      if (skillCounts[skill.id] !== undefined) {
        skillCounts[skill.id] += 1;
      }
    });
  });

  const maxValue = Math.max(...Object.values(skillCounts), 0);
  const fullMark = maxValue > 0 ? Math.ceil(maxValue * safetyFactor) : 100;

  return skillProperties.map((prop) => ({
    skill: prop.name || prop.nameKey,
    value: skillCounts[prop.id] || 0,
    fullMark,
  }));
}

export function getSummarizedSkillsRadarChartDataFromTrainings(
  trainings: ITraining[] | null | undefined,
  exercises: IExercise[] | undefined,
  exerciseProperties: IExerciseProperty[] | undefined,
): ISkillsRadarChartData[] {
  if (!trainings || trainings.length === 0 || !exerciseProperties) {
    return [];
  }

  const skillProperties = exerciseProperties.filter(
    (prop) => prop.type === ExercisePropertyType.developedSkill,
  );

  if (skillProperties.length === 0) {
    return [];
  }

  const aggregated: Record<string, number> = {};
  skillProperties.forEach((prop) => {
    aggregated[prop.id] = 0;
  });

  trainings.forEach((training) => {
    const exerciseIds = getUniqueExerciseIdsWithFinishedWork(training);
    exerciseIds.forEach((exerciseId) => {
      const exercise = exercises?.find((e) => e.id === exerciseId);
      exercise?.developedSkills?.forEach((skill) => {
        if (aggregated[skill.id] !== undefined) {
          aggregated[skill.id] += 1;
        }
      });
    });
  });

  const maxValue = Math.max(...Object.values(aggregated), 0);
  const fullMark =
    maxValue > 0 ? Math.ceil(maxValue * skillsSafetyFactorSummarize) : 100;

  return skillProperties.map((prop) => ({
    skill: prop.name || prop.nameKey,
    value: aggregated[prop.id] || 0,
    fullMark,
  }));
}
